import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getServerURL } from '../../../utils/funcs/get-serverURL';
import style from './admin.module.css';

export default function LegacyCertificatesUpload() {
  const { getToken } = useAuth();
  const serverURL = getServerURL();
  
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = await getToken();

      const response = await fetch(`${serverURL}/admin/legacy-certificates/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Upload failed';
        
        // If there's a summary, show it
        if (errorData.summary) {
          console.log('Debug info:', errorData.summary);
          if (errorData.details && errorData.details.length > 0) {
            throw new Error(`${errorMessage}\n\nDetails:\n${errorData.details.join('\n')}`);
          }
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setUploadResult(result);
      setFile(null);

      // Reset file input
      const fileInput = document.getElementById('legacy-cert-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ maxWidth: '900px' }}>
      <h2 style={{ marginBottom: '20px', color: '#333' }}>Legacy Certificate Data Upload</h2>

      {/* File Upload Section */}
      <div
        style={{
          border: '2px dashed #00adb5',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          marginBottom: '30px',
          backgroundColor: '#f0fafb',
        }}
      >
        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="legacy-cert-file-input"
            style={{
              display: 'inline-block',
              padding: '12px 30px',
              backgroundColor: '#00adb5',
              color: 'white',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#009ba3')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#00adb5')}
          >
            Choose Excel File
          </label>
          <input
            id="legacy-cert-file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </div>

        {file ? (
          <p style={{ margin: '15px 0', color: '#333', fontSize: '1.1em' }}>
            <strong>Selected:</strong> {file.name}
          </p>
        ) : (
          <p style={{ margin: '15px 0', color: '#666' }}>
            No file selected. Accepted formats: .xlsx, .xls
          </p>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          style={{
            padding: '12px 40px',
            backgroundColor: file && !uploading ? '#00adb5' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1em',
            fontWeight: '600',
            cursor: file && !uploading ? 'pointer' : 'not-allowed',
            marginTop: '10px',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            if (file && !uploading) {
              e.currentTarget.style.backgroundColor = '#009ba3';
            }
          }}
          onMouseLeave={(e) => {
            if (file && !uploading) {
              e.currentTarget.style.backgroundColor = '#00adb5';
            }
          }}
        >
          {uploading ? 'Uploading...' : 'Upload Certificates'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: '6px',
            padding: '15px',
            marginBottom: '20px',
            color: '#c62828',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.95em',
            fontFamily: 'monospace',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Success Message */}
      {uploadResult && (
        <div
          style={{
            backgroundColor: '#e8f5e9',
            border: '1px solid #4caf50',
            borderRadius: '6px',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ margin: '0 0 15px 0', color: '#2e7d32' }}>✅ Upload Successful</h3>
          <p style={{ margin: '8px 0', color: '#333' }}>
            <strong>Imported:</strong> {uploadResult.imported} certificates
          </p>
          <p style={{ margin: '8px 0', color: '#333' }}>
            <strong>Skipped:</strong> {uploadResult.skipped || 0} rows
          </p>
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #81c784' }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#d32f2f' }}>
                Issues found:
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#555' }}>
                {uploadResult.errors.map((error: string, idx: number) => (
                  <li key={idx} style={{ marginBottom: '5px', fontSize: '0.95em' }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
